/**
 * Base service with common functionality for truck scheduling
 * Supports both inbound and outbound truck domains
 */

// Import auth utilities
import { getCurrentUserId as getUserId, getAuthToken } from '../../../../../utils/authUtils';

// Base URL for API
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Helper to get authorization headers with JWT token from localStorage
 * @returns {Object} Headers object with Content-Type and Authorization
 */
const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

/**
 * Helper to get current user ID from JWT token
 * @returns {string} User ID or 'system' if not found
 */
const getCurrentUserId = () => {
  return getUserId(); // Use the centralized auth utility function
};

/**
 * Standard error handling for API requests
 * @param {Error} error - The error that occurred
 * @param {Object} responseData - Additional error data from the API response
 * @returns {Promise} Rejected promise with error message and data
 */
const handleError = (error, responseData = null) => {
  // Create a custom error object with both the message and response data
  const customError = new Error(error.message || 'An error occurred');
  
  // If we have additional data from the response, add it to the error object
  if (responseData) {
    Object.assign(customError, responseData);
  }
  
  if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
    customError.message = 'Network error. Please check your connection.';
  }
  
  return Promise.reject(customError);
};

/**
 * Build query string from filters object
 * @param {Object} filters - Key/value pairs for filtering
 * @returns {string} URL query string (with leading ? if non-empty)
 */
const buildQueryString = (filters = {}) => {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }
  
  // Filter out undefined and null values
  const validFilters = Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
  
  const queryParams = new URLSearchParams(validFilters).toString();
  return queryParams ? `?${queryParams}` : '';
};

/**
 * Base service with common CRUD operations for truck scheduling
 */
const truckService = {
  /**
   * Get all items from an endpoint with optional filtering
   * @param {string} endpoint - API endpoint path
   * @param {Object} filters - Optional filters to apply
   * @returns {Promise<Object>} Promise resolving to API response
   */
  getAll: async (endpoint, filters = {}) => {
    try {
      const queryString = buildQueryString(filters);
      const url = `${API_URL}/${endpoint}${queryString}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Get a single item by ID
   * @param {string} endpoint - API endpoint path
   * @param {string} id - Item ID to retrieve
   * @returns {Promise<Object>} Promise resolving to API response
   */
  getById: async (endpoint, id) => {
    try {
      const response = await fetch(`${API_URL}/${endpoint}/${id}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Create a new item
   * @param {string} endpoint - API endpoint path
   * @param {Object} data - Data for the new item
   * @returns {Promise<Object>} Promise resolving to API response
   */
  create: async (endpoint, data) => {
    try {
      // Get the current user ID
      const userId = getCurrentUserId();
      
      // Add user ID to the data payload
      const enhancedData = {
        ...data,
        created_by: data.created_by || userId,
        updated_by: data.updated_by || userId
      };
      
      const response = await fetch(`${API_URL}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(enhancedData)
      });
      
      if (!response.ok) {
        // Parse the error response to get detailed validation errors
        const errorData = await response.json().catch(() => ({}));
        
        // Create a custom error with both the message and any additional data
        const errorMessage = errorData.message || `Error ${response.status}: ${response.statusText}`;
        const error = new Error(errorMessage);
        
        // Add additional fields from the response to the error object
        if (errorData.required) {
          error.required = errorData.required;
        }
        
        throw error;
      }
      
      return await response.json();
    } catch (error) {
      // Get the response data from the API if available
      let responseData = {};
      try {
        // Check if this was an API error with a structured response
        if (error.response && error.response.json) {
          responseData = await error.response.json();
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      return handleError(error, responseData);
    }
  },
  
  /**
   * Update an existing item
   * @param {string} endpoint - API endpoint path
   * @param {string} id - Item ID to update
   * @param {Object} data - Updated data
   * @returns {Promise<Object>} Promise resolving to API response
   */
  update: async (endpoint, id, data) => {
    try {
      // Get the current user ID
      const userId = getCurrentUserId();
      
      // Add user ID to the data payload
      const enhancedData = {
        ...data,
        updated_by: data.updated_by || userId
      };
      
      const response = await fetch(`${API_URL}/${endpoint}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(enhancedData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Delete an item
   * @param {string} endpoint - API endpoint path
   * @param {string} id - Item ID to delete
   * @returns {Promise<Object>} Promise resolving to API response
   */
  delete: async (endpoint, id) => {
    try {
      const response = await fetch(`${API_URL}/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Update an item's status
   * @param {string} endpoint - API endpoint path
   * @param {string} id - Item ID
   * @param {string} status - New status
   * @param {Object} additionalData - Additional data to include with status update
   * @returns {Promise<Object>} Promise resolving to API response
   */
  updateStatus: async (endpoint, id, status, additionalData = {}) => {
    try {
      const statusData = {
        status,
        ...additionalData,
        updated_at: new Date().toISOString()
      };
      
      const response = await fetch(`${API_URL}/${endpoint}/${id}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(statusData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Get statistics for an endpoint
   * @param {string} endpoint - API endpoint path
   * @returns {Promise<Object>} Promise resolving to API response
   */
  getStats: async (endpoint) => {
    try {
      const response = await fetch(`${API_URL}/${endpoint}/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      return handleError(error);
    }
  },
  
  /**
   * Factory method to create a specialized truck service based on domain
   * @param {string} domain - 'inbound' or 'outbound'
   * @param {Object} customMethods - Custom methods specific to the domain
   * @returns {Object} A specialized truck service
   */
  getServiceForDomain: (domain) => {
    // Determine endpoint based on domain
    const endpoint = domain === 'inbound' ? 'inbound-trucks' : 'outbound-trucks';
    
    return {
      // Standard methods with consistent naming
      getAllTrucks: (filters = {}) => truckService.getAll(endpoint, filters),
      getTruckById: (id) => truckService.getById(endpoint, id),
      createTruck: (truckData) => {
        const userId = getCurrentUserId();
        const data = {
          ...truckData,
          created_at: new Date().toISOString(),
          created_by: truckData.created_by || userId,
          updated_by: truckData.updated_by || userId
        };
        return truckService.create(endpoint, data);
      },
      updateTruck: (id, truckData) => {
        const userId = getCurrentUserId();
        const data = {
          ...truckData,
          updated_at: new Date().toISOString(),
          updated_by: truckData.updated_by || userId
        };
        return truckService.update(endpoint, id, data);
      },
      deleteTruck: (id) => truckService.delete(endpoint, id),
      updateTruckStatus: (id, status, additionalData = {}) => {
        const userId = getCurrentUserId();
        const enhancedData = {
          ...additionalData,
          updated_by: additionalData.updated_by || userId
        };
        return truckService.updateStatus(endpoint, id, status, enhancedData);
      },
      getTruckStats: () => truckService.getStats(endpoint),
      
      // Domain-specific methods
      ...(domain === 'inbound' ? {
        markTruckAsArrived: (id, arrivalData) => {
          return truckService.update(endpoint, id, {
            ...arrivalData,
            arrival_datetime: arrivalData.arrival_datetime || new Date().toISOString(),
            status: 'Arrived'
          });
        },
        startReceiving: (id) => {
          return truckService.update(endpoint, id, {
            started_at: new Date().toISOString(),
            status: 'Received'
          });
        },
        completeReceiving: (id, receivingData) => {
          return truckService.update(endpoint, id, {
            ...receivingData,
            completed_at: new Date().toISOString(),
            status: 'Putaway'
          });
        }
      } : {
        startStaging: (id) => {
          return truckService.update(endpoint, id, {
            staging_start: new Date().toISOString(),
            status: 'Staged'
          });
        },
        completeLoading: (id, loadingData) => {
          return truckService.update(endpoint, id, {
            ...loadingData,
            loading_complete: new Date().toISOString(),
            status: 'Loaded'
          });
        },
        markAsShipped: (id, shippingData) => {
          return truckService.update(endpoint, id, {
            ...shippingData,
            departure_datetime: new Date().toISOString(),
            status: 'Shipped'
          });
        }
      })
    };
  },
  
  // Utility functions
  getAuthHeaders,
  handleError,
  buildQueryString,
  getCurrentUserId
};

export default truckService;